import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Box,
  IconButton,
  Button,
  Snackbar,
  Tooltip,
  TextField,
} from "@mui/material";
import {
  CopyAll as CopyIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { SAAS_SITE_URL } from "../../utils";
import PsContext from "../../context";
import QRCode from "qrcode";

const SchoolLoginDetails = (props) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [urlCopySuccess, setUrlCopySuccess] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrValue, setQrValue] = useState("");
  const context = useContext(PsContext);

  const domainName = context.userData.domain_name
    ? `https://${context.userData.domain_name}/`
    : SAAS_SITE_URL;

  const schoolData = {
    schoolAdminUrl: `${domainName}${props.dataSource.school_prefix}/admin`,
    studentParentUrl: `${domainName}${props.dataSource.school_prefix}`,
    schoolAdminUsername: props.loginDetails.username,
    schoolAdminPassword: props.loginDetails.password,
    studentParentUsername: "Student's Mobile Number / Registration Number",
    studentParentPassword: "Date of Birth",
  };

  // Function to copy only the text content (excluding QR Code)
  const copyToClipboard = () => {
    const content = document.getElementById("copyableContent").innerText;
    navigator.clipboard.writeText(content).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  useEffect(() => {
    if (schoolData.studentParentUrl) {
      const value = schoolData.studentParentUrl;
      setQrValue(value);

      QRCode.toDataURL(value, { width: 200, margin: 2 }, (err, url) => {
        if (err) {
          console.error(err);
          return;
        }
        setQrCodeUrl(url);
      });
    }
  }, [schoolData.studentParentUrl]);

  const handleUrlCopy = () => {
    navigator.clipboard.writeText(qrValue).then(() => {
      setUrlCopySuccess(true);
      setTimeout(() => setUrlCopySuccess(false), 2000);
    });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = "school_qr_code.png";
    link.click();
  };

  return (
    <div>
      {/* Copy button */}
      <Box display="flex" justifyContent="flex-end">
        <IconButton onClick={copyToClipboard}>
          <CopyIcon />
        </IconButton>
      </Box>

      {/* Copied Snackbar */}
      <Snackbar
        open={copySuccess}
        message="Content copied to clipboard!"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={2000}
      />

      <div id="copyableContent">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  backgroundColor: "#1976d2",
                  padding: "10px 15px",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  gutterBottom
                  sx={{ color: "white" }}
                >
                  You can access the school software using the following URLs
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* School Admin Login Card */}
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#f5f5f5", boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  For School Admin Login
                </Typography>
                <Divider style={{ margin: "10px 0" }} />
                <Typography variant="body1">
                  🔗{" "}
                  <a
                    href={schoolData.schoolAdminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3f51b5", fontWeight: "bold" }}
                  >
                    {schoolData.schoolAdminUrl}
                  </a>
                </Typography>
                <Typography variant="body1">
                  <strong>Username:</strong> {schoolData.schoolAdminUsername}
                </Typography>
                <Typography variant="body1">
                  <strong>Password:</strong> {schoolData.schoolAdminPassword}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Student/Parent Login Card */}
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#e0f7fa", boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  For Student / Parent Login
                </Typography>
                <Divider style={{ margin: "10px 0" }} />
                <Typography variant="body1">
                  🔗{" "}
                  <a
                    href={schoolData.studentParentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#00796b", fontWeight: "bold" }}
                  >
                    {schoolData.studentParentUrl}
                  </a>
                </Typography>
                <Typography variant="body1">
                  <strong>Username:</strong> {schoolData.studentParentUsername}
                </Typography>
                <Typography variant="body1">
                  <strong>Password:</strong> {schoolData.studentParentPassword}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      {/* QR Code Section (Not Included in Copyable Content) */}
      <Grid item xs={12}>
        <Card sx={{ backgroundColor: "#fbe9e7", boxShadow: 3, p: 2 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              QR Code for Student / Parent Login
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* QR Code Display */}
            {qrCodeUrl ? (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <img
                  src={qrCodeUrl}
                  alt="QR Code"
                  style={{ width: 150, height: 150 }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Generating QR Code...
              </Typography>
            )}

            {/* Display Actual URL */}
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={qrValue}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2 }}
            />

            {/* Action Buttons */}
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Tooltip title="Copy URL">
                  <IconButton onClick={handleUrlCopy} color="primary">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Grid>

              <Grid item>
                <Tooltip title="Download QR Code">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Copy Success Snackbar */}
            <Snackbar
              open={urlCopySuccess}
              autoHideDuration={2000}
              message="URL copied to clipboard!"
            />
          </CardContent>
        </Card>
      </Grid>
    </div>
  );
};

export default SchoolLoginDetails;
